import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Row from "react-bootstrap/Row";
import Card from "react-bootstrap/Card";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";

import IconSvg from "../../../SharedComponents/IconSvg";
import Comment from "./CommentSection/Comment";
import { getCandidateById, getInterviewFeedback } from "../Store/Thunk";
import { SetSelectedComment, ClearCandidateState } from "../Store";
import AdminBreadCrumb from "../../AdminBreadCrumb";
import { SetSelectedOptionByCandidateId } from "../../Project/Store";
import {
  ChangeIsUpdateOperation,
  SetUpsertModalCandidate,
  ShowUpsertCandidateModal,
} from "../../CreateCandidate/Store";
import UpsertCandidateModal from "../../CreateCandidate/Components/UpsertCandidateModal";
import DemographicInfoColumn from "./DemographicInfoColumn";
import { SetSelectedPage } from "../../../AppMenu/Store";
import { getCompanyCreateProjectData } from "../../Project/Store/Thunk";
import AssignedAssessments from "./AssessmentsSection/AssignedAssessments";
import SessionsModal from "./AssessmentsSection/Modals/SessionsModal";
import Overlay from "../../../SharedComponents/Overlay";
import CvUploadButton from "./CvUploadButton";
import ProfilePicture from "../../../SharedComponents/ProfilePicture";
import InterviewFeedbackModal from "./InterviewFeedbackModal";

const Candidate = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const isLoading = useSelector((state) => state.candidate.isLoading);
  const candidate = useSelector((state) => state.candidate.candidate);
  const comments = useSelector((state) => state.candidate.comments);
  const tickets = useSelector((state) => state.candidate.candidate.tickets);
  const positionList = useSelector((state) => state.createPosition.positionList);
  const projectCandidates = useSelector((state) => state.projects.candidates);
  const cvFileName = useSelector((state) => state.candidate.candidate.cvFileName);

  const { state } = useLocation();
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [showInterviewFeedbackModal, setShowInterviewFeedbackModal] = useState(false);

  useEffect(() => {
    dispatch(getCandidateById({ id: state.candidateId }));
    dispatch(getInterviewFeedback({ id: state.candidateId }));
    dispatch(SetSelectedComment(null));
    if (projectCandidates.length === 0) {
      dispatch(getCompanyCreateProjectData());
    }

    // Cleanup: clear candidate state when component unmounts
    return () => {
      dispatch(ClearCandidateState());
    };
  }, [state.candidateId, dispatch]);

  const handleUpdateCandidate = () => {
    dispatch(SetUpsertModalCandidate({ ...candidate, candidateName: candidate.name }));
    setSelectedCandidate({ ...candidate, candidateName: candidate.name });
    dispatch(ShowUpsertCandidateModal({ upsertCandidateModalIsShown: true }));
    dispatch(ChangeIsUpdateOperation({ isUpdateOperation: true }));
  };

  return (
    <>
      {isLoading && <Overlay />}
      <Row className="ms-0 custom-candidate-margin">
        <Col xs="auto" className="align-content-center">
          <AdminBreadCrumb
            pageName={t("candidateBreadCrumbLabel")}
            backPageName={t("createCandidateBreadCrumbLabel")}
            backUrl={state.backUrl === "/candidate/" ? "/dash" : state.backUrl}
          />
          <Row className="justify-content-center d-md-none mb-3">
            <Col xs="auto" className="align-content-center">
              <Button
                onClick={handleUpdateCandidate}
                className="button secondary"
                size="sm"
                style={{ height: 46, width: 46, padding: "4px", borderRadius: "10px" }}
              >
                <IconSvg icon="edit" fill="#0077B6" />
              </Button>
            </Col>
          </Row>
        </Col>

        <Col className="align-content-center">
          <Row className="h-100">
            <Col className="mb-3">
              <Card className="breadcrumb-card h-100">
                <Row className="h-100 mx-0 text-20 text-color-gray-dark fw-medium">
                  <Col xs={12} md={6} className="align-content-center">
                    <Row>
                      <Col xs="auto" className="pe-0 align-content-center text-16 text-color-gray-light">
                        {t("candidateFullNameLabel")}
                      </Col>
                      <Col>{candidate.name}</Col>
                    </Row>
                  </Col>
                  <Col xs={12} md={6} className="align-content-center">
                    <Row>
                      <Col xs="auto" className="pe-0 align-content-center text-16 text-color-gray-light">
                        {t("candidateEmailLabel")}
                      </Col>
                      <Col>{candidate.email}</Col>
                    </Row>
                  </Col>
                </Row>
              </Card>
            </Col>
            <Col xs="auto" className="d-none d-md-block">
              <Button
                onClick={handleUpdateCandidate}
                className="button secondary"
                size="sm"
                style={{ height: 46, width: 46, padding: "4px", borderRadius: "10px" }}
              >
                <IconSvg icon="edit" fill="#0077B6" />
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>

      <Row className="ms-0 custom-candidate-margin">
        <Col>
          <Card className="default-card py-4">
            <Col className="align-content-center">
              <Row className="mx-0">
                <Col xs={12} md="auto" className="align-content-center ms-md-5">
                  <Row className="justify-content-center">
                    <ProfilePicture
                      userId={candidate.applicationUserId}
                      profilePicFileName={candidate.profilePic}
                      size={100}
                    />
                  </Row>
                </Col>

                <Col className="ms-3 ms-xl-5">
                  <DemographicInfoColumn candidate={candidate} isCollapsible />
                </Col>

                <Col xs={12} md="auto" className="align-content-center px-3 mt-md-0 mt-2 border-end" style={{ borderColor: "#E7ECEF" }}>
                  <Row className="justify-content-center gap-2">
                    <Col xs="auto">
                      <Button
                        className="ai-assessment-button"
                        onClick={() => {
                          // AI Assessment action
                          console.log("AI Assessment clicked");
                        }}
                      >
                        AI
                      </Button>
                    </Col>
                    <Col xs="auto">
                      <Button
                        className="button secondary"
                        style={{ height: 100, width: 100, padding: "8px", borderRadius: "10px" }}
                        onClick={() => setShowInterviewFeedbackModal(true)}
                      >
                        <div className="d-flex flex-column align-items-center justify-content-center h-100">
                          <div style={{ fontSize: "24px", marginBottom: "4px" }}>★</div>
                          <div style={{ fontSize: "12px", textAlign: "center", lineHeight: "1.2" }}>
                            {t("interviewFeedback")}
                          </div>
                        </div>
                      </Button>
                    </Col>
                  </Row>
                </Col>

                <Col xs={12} xxl="auto" className="align-content-center px-4 mt-xxl-0 mt-2">
                  <Row className="justify-content-center">
                    <Col xs="auto" className="align-content-center">
                      <CvUploadButton candidateId={candidate.candidateId} cvNavigateIsShown cvFileName={cvFileName} />
                    </Col>
                    <Col xs="auto" className="d-none d-sm-block d-xxl-none">
                      <Button
                        className="button secondary h-100"
                        onClick={() => {
                          dispatch(SetSelectedOptionByCandidateId({ value: candidate.candidateId }));
                          navigate("/project", {
                            state: {
                              candidateId: candidate.candidateId,
                              backUrl: "/Candidate/",
                            },
                          });
                          dispatch(SetSelectedPage({ url: "/project/" }));
                        }}
                      >
                        {t("sendAssessmentButtonLabel")}
                      </Button>
                    </Col>
                  </Row>
                  <Row className="justify-content-center mt-2 d-sm-none d-xxl-block">
                    <Col xs="auto">
                      <Button
                        className="button secondary h-100"
                        onClick={() => {
                          dispatch(SetSelectedOptionByCandidateId({ value: candidate.candidateId }));
                          navigate("/project", {
                            state: {
                              candidateId: candidate.candidateId,
                              backUrl: "/Candidate/",
                            },
                          });
                          dispatch(SetSelectedPage({ url: "/project/" }));
                        }}
                      >
                        {t("sendAssessmentButtonLabel")}
                      </Button>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Col>
          </Card>
        </Col>
      </Row>

      <Row>
        {tickets && (
          <Col xs={12} xxl={7} className="pe-0">
            <AssignedAssessments />
          </Col>
        )}
        {comments && (
          <Col xs={12} xxl={5}>
            <Comment candidate={candidate} />
          </Col>
        )}
      </Row>

      {/*<UpsertCommentModal />*/}
      <UpsertCandidateModal
        positionList={positionList}
        backUrl={state.backUrl}
        selectedCandidate={selectedCandidate}
        setSelectedCandidate={setSelectedCandidate}
      />
      <SessionsModal />
      <InterviewFeedbackModal
        show={showInterviewFeedbackModal}
        onHide={() => setShowInterviewFeedbackModal(false)}
        candidateId={candidate.candidateId}
        candidateName={candidate.name}
      />
    </>
  );
};

export default Candidate;
